import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, Skeleton } from '@/components/ui';
import { useUserGradesQuery } from '../grades/hooks/useUserGradesQuery';
import { useUserGradeAssignMutation } from '../hooks/useUserGradeAssignMutation';

interface UserGradePanelProps {
  userId: string;
}

export const UserGradePanel = ({ userId }: UserGradePanelProps) => {
  const { t } = useTranslation('common');
  const query = useUserGradesQuery({ size: 50 });
  const assignMutation = useUserGradeAssignMutation();
  const [selectedGradeId, setSelectedGradeId] = useState('');
  const [reason, setReason] = useState('');
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const activeGrades = (query.data?.content ?? []).filter((g) => g.active);
  // QA-395 — BE(AdminGradeChangeRequest)는 사유를 필수(@NotBlank)로 받는다. 종전엔 "(선택)"으로 비워 보내 400 이었다.
  const reasonValid = reason.trim() !== '' && reason.trim().length <= 255;

  const handleAssign = async () => {
    if (!selectedGradeId || !reasonValid) return;
    await assignMutation.mutateAsync({ userId, gradeId: selectedGradeId, reason: reason.trim() });
    const grade = activeGrades.find((g) => g.gradeId === selectedGradeId);
    setSuccessMsg(t('users.grade.assignSuccess', { name: grade?.gradeName ?? '' }));
    setSelectedGradeId('');
    setReason('');
  };

  if (query.isPending) {
    return (
      <div className="space-y-2" role="status" aria-live="polite">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[44px] w-full" />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <p className="py-10 text-center text-sm" style={{ color: 'var(--status-danger-fg)' }}>
        {t('users.grade.loadFailed')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {successMsg && (
        <div
          className="rounded-xl px-4 py-3 text-sm"
          style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
        >
          {successMsg}
        </div>
      )}

      {assignMutation.isError && (
        <div
          className="rounded-xl px-4 py-3 text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          {t('users.grade.assignFailed')}
        </div>
      )}

      <div
        className="rounded-xl p-4 space-y-3"
        style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      >
        <p className="text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
          {t('users.grade.assignTitle')}
        </p>

        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-1 flex-1" style={{ minWidth: '160px' }}>
            <label className="text-xs font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('users.grade.selectGrade')}
            </label>
            <select
              value={selectedGradeId}
              onChange={(e) => setSelectedGradeId(e.target.value)}
              disabled={assignMutation.isPending || activeGrades.length === 0}
              className="rounded-xl px-3 py-2 text-sm outline-none"
              style={{
                background: 'var(--surface-1)',
                border: '1px solid var(--divider-soft)',
                color: 'var(--content-default)',
              }}
            >
              <option value="">{t('users.grade.selectPlaceholder')}</option>
              {activeGrades.map((g) => (
                <option key={g.gradeId} value={g.gradeId}>
                  {g.gradeName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 flex-1" style={{ minWidth: '160px' }}>
            <label className="text-xs font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('users.grade.reason')}
              <span style={{ color: 'var(--signal-danger)' }}> *</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('users.grade.reasonPlaceholder')}
              maxLength={255}
              aria-label={t('users.grade.reason')}
              data-testid="grade-assign-reason"
              disabled={assignMutation.isPending}
              className="rounded-xl px-3 py-2 text-sm outline-none"
              style={{
                background: 'var(--surface-1)',
                border: '1px solid var(--divider-soft)',
                color: 'var(--content-default)',
              }}
            />
          </div>

          <Button
            size="sm"
            onClick={() => void handleAssign()}
            disabled={!selectedGradeId || !reasonValid || assignMutation.isPending}
            data-testid="grade-assign-submit"
          >
            {t('users.grade.assign')}
          </Button>
        </div>

        {activeGrades.length === 0 && (
          <p className="text-xs" style={{ color: 'var(--content-muted)' }}>
            {t('users.grade.noGrades')}
          </p>
        )}
      </div>

      {(query.data?.content.length ?? 0) > 0 && (
        <div
          className="overflow-hidden rounded-xl"
          style={{ border: '1px solid var(--divider-strong)' }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--surface-1)', borderBottom: '1px solid var(--divider-strong)' }}>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                  {t('userGrades.cols.gradeName')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                  {t('userGrades.cols.gradeCode')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                  {t('userGrades.cols.priority')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                  {t('userGrades.cols.memberCount')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                  {t('userGrades.cols.active')}
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data?.content.map((grade) => (
                <tr
                  key={grade.gradeId}
                  style={{ borderBottom: '1px solid var(--divider-soft)' }}
                >
                  <td className="px-4 py-3 font-medium" style={{ color: 'var(--content-strong)' }}>
                    {grade.gradeName}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--content-default)' }}>
                    {grade.gradeCode}
                  </td>
                  <td className="px-4 py-3" style={{ color: 'var(--content-default)' }}>
                    {grade.priority}
                  </td>
                  <td className="px-4 py-3" style={{ color: 'var(--content-default)' }}>
                    {grade.memberCount.toLocaleString('ko-KR')}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-medium"
                      style={{
                        background: grade.active ? 'var(--status-success-bg)' : 'var(--surface-1)',
                        color: grade.active ? 'var(--status-success-fg)' : 'var(--content-muted)',
                      }}
                    >
                      {grade.active ? t('cms.active') : t('cms.inactive')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
